import bind from 'bind-decorator';
import { Light } from '@babylonjs/core/Lights/light';
import { Nullable } from '@babylonjs/core/types';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import App from '../App';
import { ViewHeightType } from '../tool/Person';

import { captureException } from '@egr/xbox/utils/Error';
import { corsProxyUrl } from '@egr/xbox/utils/Services';
import { getEnvVar } from '@egr/xbox/utils/ReactScriptHelper';
import { ignoreAjaxErrorHandlerMessageCallback } from '@egr/xbox/utils/AjaxErrorHandler';
import { withProgress } from '@egr/xbox/progress-manager/ProgressManager';
import { getFileList } from '@egr/xbox/utils/File';

import { ajax } from '@egr/wcf/modules/utils/async';
import { isNotNullOrEmpty } from '@egr/wcf/modules/utils/string';
import { DocumentContainer, ZipDocumentContainer } from '@egr/wcf/modules/core/io';
import { CameraMode } from '@egr/wcf/modules/core/view/CameraControl';
import { BoundingBox, SceneElement } from '@egr/wcf/modules/core/mdl';
import { isNumber } from '@egr/wcf/modules/utils/number';
import { GroupedMap } from '@egr/wcf/modules/utils';
import { ERR_FAILED } from '@egr/wcf/modules/utils/error';
import { contains } from '@egr/wcf/modules/utils/array';
import { placeElementsInViewCenter } from '@egr/wcf/modules/pl/utils';
import { InsertElements } from '@egr/wcf/modules/core/cmd';
import { importPec } from '@egr/wcf/modules/cf/io/PecImport';
import { loadDocumentFromZip } from '@egr/wcf/modules/core';
import { ArticleManager } from '@egr/wcf/modules/cf';
import { getFileExtension } from '@egr/xbox/utils/String';

export class StartupActions {

    constructor(protected app: App) {
        this.importPBOX = this.importPBOX.bind(this);
        this.importPEC = this.importPEC.bind(this);
        this.loadPbox = this.loadPbox.bind(this);
        this.loadFileFromDropEvent = this.loadFileFromDropEvent.bind(this);

        this._loadFile = this._loadFile.bind(this);
    }

    public async _loadFile(): Promise<void> {
        const files: FileList | null = await getFileList('.opl,.pbox,.obk,.pec,.eox');
        if (files != null && files.length === 1) {
            const extension: string = getFileExtension(files[0].name).toLocaleLowerCase();
            switch (extension) {
                case 'pbox': return this.loadPbox(files.item(0));
                case 'opl': return this.loadPbox(files.item(0));
                case 'pec': return void this.loadPecFile(files.item(0)!);
            }
        }
    }

    public loadFileFromDropEvent(event: DragEvent) {
        event.preventDefault();

        if (
            event.dataTransfer?.files.length === 1 &&
            /pbox$/i.test(event.dataTransfer.files[0].name)
        ) {
            void this.loadPbox(event.dataTransfer.files[0]);
        }

    }

    @withProgress('import pbox')
    public async importPBOX(importUrl: string): Promise<void> {
        if (isNotNullOrEmpty(importUrl)) {
            const buffer: ArrayBuffer = await this.getContent(importUrl);
            const container: Blob = new Blob([buffer], {type: 'application/zip'});
            await this.loadPbox(container);
        }
    }

    @withProgress('import pec')
    public async importPEC(importUrl: string): Promise<void> {
        if (isNotNullOrEmpty(importUrl)) {
            const buffer: ArrayBuffer = await this.getContent(importUrl);
            await this.loadPecFile(buffer);
        }
    }

    private async getContent(url: string): Promise<ArrayBuffer> {
        try {
            return await ajax<ArrayBuffer>(
                'GET',
                url,
                null,
                { responseType: 'arraybuffer', retryAttempts: 1 },
                ignoreAjaxErrorHandlerMessageCallback
            );
        } catch (e) {
            captureException(e as Error, 'debug');
            const proxyUrl: string | null = corsProxyUrl(url);
            if (isNotNullOrEmpty(proxyUrl)) {
                return await ajax<ArrayBuffer>('GET', proxyUrl, null, {
                    responseType: 'arraybuffer'
                });
            }
            throw e;
        }
    }

    private async loadPecFile(pecFile: Blob | ArrayBuffer | File): Promise<Array<SceneElement>> {
        const articleManager: ArticleManager = await this.app.sceneManager.getArticleManager();
        const elements: Array<SceneElement> = await importPec(articleManager, pecFile);
        placeElementsInViewCenter(elements, this.app.coreApp.viewer.view, this.app.coreApp.model);

        const insertCommand: InsertElements = new InsertElements(this.app.coreApp, elements);
        void this.app.coreApp.commands.executeCommand(insertCommand);
        this.app.coreApp.viewer.requestRenderFrame();

        return elements;
    }

    private async importOBX(pcontainerSolution: DocumentContainer): Promise<boolean> {
        if (pcontainerSolution == null) {
            throw new Error(ERR_FAILED);
        }
        const tFiles: Array<string> = await pcontainerSolution.listFiles();

        try {
            const tObkFileName: string = 'resources/project.obk';
            if (contains(tFiles, tObkFileName)) {
                const tprojectOBK: ArrayBuffer = await pcontainerSolution.getFile(tObkFileName, true);

                if (tprojectOBK != null) {
                    const tcontainerOBK: ZipDocumentContainer = await ZipDocumentContainer.LoadContainer(tprojectOBK);

                    if (tcontainerOBK != null) {
                        const tOBKFiles: Array<string> = await tcontainerOBK.listFiles();

                        try {
                            const tObxFileName: string = '1.bsk';
                            if (contains(tOBKFiles, tObxFileName)) {
                                const tprojectOBX: string = await tcontainerOBK.getFile(tObxFileName, false);

                                if (tprojectOBX != null) {
                                    this.parseOBX(tprojectOBX);
                                    return (Promise.resolve(true));
                                }
                            }
                        } catch (error) {
                            console.error(error);
                        }
                    }
                }
            }
        } catch (error) {
            console.error(error);
        }

        return (Promise.resolve(false));
    }

    private parseOBX(pXML: string): void {
        if (!window.DOMParser || pXML.trim() === '') {
            return;
        }

        const tParser: DOMParser = new DOMParser();
        let tSceneOBX: Document | null = null;
        tSceneOBX = tParser.parseFromString(pXML, 'text/xml');

        if (tSceneOBX == null) {
            return;
        }

        const tbskArticles: HTMLCollectionOf<Element> = tSceneOBX.getElementsByTagName('bskArticle');
        const tusrArticles: HTMLCollectionOf<Element> = tSceneOBX.getElementsByTagName('usrArticle');

        const tartNodes: Array<Element> = [];

        for (let i: number = 0; i < tbskArticles.length; i++ ) {
            tartNodes.push(tbskArticles[i]);
        }

        for (let i: number = 0; i < tusrArticles.length; i++ ) {
            tartNodes.push(tusrArticles[i]);
        }

        for (const telem of tartNodes) {
            if (!telem.hasAttribute('basketId') && !telem.hasAttribute('itemType')) {
                continue;
            }

            const tbasketId: string | null = telem.getAttribute('basketId');
            const titemType: string | null = telem.getAttribute('itemType');

            if (tbasketId != null && (tbasketId.trim() !== '') && titemType != null) {
                this.parseArticleNode(telem as HTMLElement, tbasketId, titemType);
            }
        }
    }

    private parseArticleNode(pNode: HTMLElement, pbasketId: string, pitemType: string): void {
        const tbasketId: string = pbasketId;
        const titemType: string = pitemType;
        let tmanId: string = '';
        let tseriesId: string = '';
        let tArtNrBase: string | undefined;
        let tArtNrFinal: string | undefined;

        const tchildNodes: NodeListOf<ChildNode> = pNode.childNodes;

        for (let i: number = 0; i < tchildNodes.length; i++) {
            const tchild: ChildNode = tchildNodes[i];

            if (tchild.nodeName.toLowerCase() === 'manufacturer') {
                if (!(tchild as HTMLElement).hasAttribute('id') ) {
                    continue;
                }

                const tvalue: string | null = (tchild as HTMLElement).getAttribute('id');
                if (tvalue != null && tvalue?.trim() !== '') {
                    tmanId = tvalue.toLowerCase();
                }
                continue;
            }

            if (tchild.nodeName.toLowerCase() === 'series') {
                if (!(tchild as HTMLElement).hasAttribute('id') ) {
                    continue;
                }

                const tvalue: string | null = (tchild as HTMLElement).getAttribute('id');
                if (tvalue != null && tvalue?.trim() !== '') {
                    tseriesId = tvalue.toLowerCase();
                }
                continue;
            }

            if (tchild.nodeName.toLowerCase() === 'artnr') {
                if (!(tchild as HTMLElement).hasAttribute('type')) {
                    continue;
                }

                let tAttrValue: string | null = (tchild as HTMLElement).getAttribute('type');

                if (tAttrValue != null && tAttrValue.trim() !== '') {
                    const tchildValue: string | null = tchild.textContent;
                    tAttrValue = tAttrValue.trim().toLowerCase();

                    if (tchildValue == null || tchildValue.trim() === '') {
                        continue;
                    }

                    switch (tAttrValue) {
                        case 'base':
                            tArtNrBase = tchildValue.trim();
                            break;
                        case 'final':
                            tArtNrFinal = tchildValue.trim();
                            break;
                        default:
                            break;
                    }
                }
            }

            if (tbasketId.trim() !== '' &&
                titemType.trim() !== '' &&
                tmanId.trim() !== '' &&
                tseriesId.trim() !== '') {
                this.app.kompanSettingsManager.addProductData(tbasketId, titemType, tmanId, tseriesId, tArtNrBase, tArtNrFinal);
            }
        }
    }

    @bind
    private restoreSunSetupFromAppData(): void {
        if (this.app.sceneManager.shadowMapping) {
            const enabled: boolean | undefined = getSunEnabledValue(this.app.coreApp.document.appData);
            this.app.sceneManager.shadowMapping.setEnabled(enabled === true);

            const altitude: number | undefined = this.app.coreApp.document.appData.getData('sun', 'altitude');
            this.app.sceneManager.shadowMapping.altitude = isNumber(altitude) ? altitude : 55;

            const azimuth: number | undefined = this.app.coreApp.document.appData.getData('sun', 'azimuth');
            this.app.sceneManager.shadowMapping.azimuth = isNumber(azimuth) ? azimuth : -66;

            const softness: number | undefined = this.app.coreApp.document.appData.getData('sun', 'softness');
            this.app.sceneManager.shadowMapping.softness = isNumber(softness) ? softness : 0.03;
        }
        const light: Nullable<Light> = this.app.coreApp.model.scene.getLightByName('shadow light');
        if (light != null) {
            light.intensity = this.app.coreApp.document.appData.getData('sun', 'intensity') ?? 1;
        }
    }

    private async loadPbox(container: Blob | File | null): Promise<void> {
        if (container == null) {
            return;
        }
        await loadDocumentFromZip(this.app.coreApp.document, container);
        const tprojectZIP: ZipDocumentContainer = await ZipDocumentContainer.LoadContainer(container);
        await this.importOBX(tprojectZIP);

        this.app.roomManager.cutOverlappingShapes();
        if (this.app.coreApp.viewer.view.cameraControl.cameraMode !== CameraMode.Perspective) {
            this.app.coreApp.model.setSelection(this.app.coreApp.model.elements);
            const boundingBox: BoundingBox = BoundingBox.FromElements(this.app.coreApp.model.selection);
            this.app.coreApp.viewer.view.cameraControl.cameraMode = CameraMode.Perspective;
            this.app.coreApp.viewer.view.cameraControl.setPosition(new Vector3(boundingBox.min.x, ViewHeightType.PARENT, boundingBox.min.z));
            this.app.coreApp.viewer.view.cameraControl.setDirection(new Vector3(boundingBox.max.x, 0, boundingBox.max.z));
            this.app.coreApp.model.setSelection(null);
        }

        const { buildingsVisible, soundData } = this.app.kompanSettingsManager.state;

        this.app.kompanSettingsManager.showGround();

        this.app.roomManager.setLayerVisibility_Buildings(buildingsVisible);

        this.app.sceneManager.initializeCameraPathInformation();

        this.app.viewManager.setInitialCamera();

        this.app.musicManager.initialize(soundData);

        this.restoreSunSetupFromAppData();

        this.app.roomManager.setAudioSpheresVisibility(false);

        this.app.coreApp.viewer.requestRenderFrame();
    }
}

function getSunEnabledValue(appData: GroupedMap): boolean | undefined {
    let enabled: boolean | undefined;

    const sunUrlParameter: string | undefined = getEnvVar('sun');

    if (sunUrlParameter === 'true' || window.location.search.includes('sun=true')) {
        enabled = true;
    } else if (sunUrlParameter === 'false' || window.location.search.includes('sun=false')) {
        enabled = false;
    }
    return enabled ?? appData.getData('sun', 'sunEnabled');
}